import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import {
    X, Play, Pause, Lock, Sparkles, HelpCircle, RotateCcw, Gauge, PencilLine,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render KaTeX with highlighted terms (red/green) and struck-through cancellations
function texHtml(latex, highlights = [], cancelTerms = []) {
    let tex = latex || '';
    cancelTerms.forEach((t) => { tex = tex.replace(t, `\\xcancel{\\color{#ef4444}${t}}`); });
    highlights.forEach((t, i) => {
        if (cancelTerms.includes(t)) return; // already cancelled
        tex = tex.replace(t, `\\textcolor{${i % 2 ? '#22c55e' : '#ef4444'}}{${t}}`);
    });
    try {
        return { __html: katex.renderToString(tex, { throwOnError: false, displayMode: true }) };
    } catch {
        return { __html: `<span>${tex}</span>` };
    }
}

// Pause-aware sleep driven by a shared control ref
function sleepControlled(ms, ctrl) {
    return new Promise((resolve) => {
        let elapsed = 0;
        let last = Date.now();
        const iv = setInterval(() => {
            const now = Date.now();
            if (!ctrl.paused) elapsed += now - last;
            last = now;
            if (ctrl.cancelled) { clearInterval(iv); resolve('cancelled'); }
            else if (elapsed >= ms) { clearInterval(iv); resolve('done'); }
        }, 50);
    });
}

export default function LiveCanvasTutor({ onExit }) {
    const navigate = useNavigate();
    const [entitlement, setEntitlement] = useState(null); // null = checking
    const [problem, setProblem] = useState('2x + 4 = 10');
    const [phase, setPhase] = useState('idle'); // idle | loading | streaming | paused | explaining | done
    const [steps, setSteps] = useState([]);
    const [stepIdx, setStepIdx] = useState(-1);
    const [wordIdx, setWordIdx] = useState(-1);
    const [speed, setSpeed] = useState(1);
    const [engine, setEngine] = useState('');
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [asking, setAsking] = useState(false);

    const ctrl = useRef({ paused: false, cancelled: false, speed: 1 });
    ctrl.current.speed = speed;

    // ---------- entitlement gate ----------
    useEffect(() => {
        axios.get(`${API}/metis/live-canvas/entitlement`, { withCredentials: true })
            .then((r) => setEntitlement(r.data))
            .catch(() => setEntitlement({ entitled: false, reason: 'auth_required' }));
    }, []);

    // ---------- SSE stream ----------
    async function startCanvas() {
        ctrl.current = { paused: false, cancelled: false, speed };
        setPhase('loading');
        setSteps([]); setStepIdx(-1); setAnswer(''); setQuestion('');
        try {
            const res = await fetch(`${API}/metis/live-canvas/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ problem }),
            });
            if (res.status === 403) { setEntitlement({ entitled: false, reason: 'upgrade_required' }); setPhase('idle'); return; }
            if (!res.ok || !res.body) throw new Error('stream failed');

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            const collected = [];
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const chunks = buffer.split('\n\n');
                buffer = chunks.pop() || '';
                for (const chunk of chunks) {
                    const ev = chunk.match(/^event: (.+)$/m)?.[1];
                    const data = chunk.match(/^data: (.*)$/m)?.[1];
                    if (!ev || !data) continue;
                    const payload = JSON.parse(data);
                    if (ev === 'meta') setEngine(payload.engine);
                    if (ev === 'step') collected.push(payload);
                }
            }
            setSteps(collected);
            setPhase('streaming');
            playSteps(collected);
        } catch (e) {
            console.error(e);
            setPhase('idle');
        }
    }

    async function playSteps(arr) {
        for (let i = 0; i < arr.length; i++) {
            if (ctrl.current.cancelled) return;
            setStepIdx(i);
            setWordIdx(-1);
            speak(arr[i].narration_script);
            const dur = (arr[i].estimated_duration_ms || 3000) / ctrl.current.speed;
            const r = await sleepControlled(dur, ctrl.current);
            if (r === 'cancelled') return;
        }
        setPhase('done');
    }

    // ---------- narration (Web Speech — swap point for Qwen3-TTS stream) ----------
    function speak(text) {
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = ctrl.current.speed;
            u.onboundary = (e) => {
                if (typeof e.charIndex === 'number') {
                    const upto = text.slice(0, e.charIndex);
                    setWordIdx(upto.trim().split(/\s+/).filter(Boolean).length);
                }
            };
            window.speechSynthesis.speak(u);
        } catch {}
    }

    function togglePause() {
        if (phase === 'streaming') {
            ctrl.current.paused = true;
            window.speechSynthesis.pause();
            setPhase('paused');
        } else if (phase === 'paused') {
            ctrl.current.paused = false;
            window.speechSynthesis.resume();
            setPhase('streaming');
        }
    }

    function stopAll() {
        ctrl.current.cancelled = true;
        window.speechSynthesis.cancel();
    }

    // ---------- Pause & Ask ----------
    async function askMetis() {
        const q = question.trim();
        if (!q || asking) return;
        setAsking(true);
        try {
            const r = await axios.post(`${API}/metis/live-canvas/ask`, {
                step_id: steps[stepIdx]?.step_id || 0,
                current_latex: steps[stepIdx]?.latex_after || problem,
                user_question: q,
            }, { withCredentials: true });
            setAnswer(r.data?.explanation || 'Let me think on that one.');
            speak(r.data?.explanation || '');
        } catch {
            setAnswer('Metis could not answer right now — resume and the next step may clarify.');
        } finally {
            setAsking(false);
        }
    }

    function openAsk() {
        ctrl.current.paused = true;
        window.speechSynthesis.pause();
        setPhase('explaining');
    }

    function resumeFromAsk() {
        setAnswer(''); setQuestion('');
        ctrl.current.paused = false;
        window.speechSynthesis.resume();
        setPhase('streaming');
    }

    const current = stepIdx >= 0 ? steps[stepIdx] : null;
    const captionWords = useMemo(
        () => (current?.narration_script || '').split(/\s+/).filter(Boolean),
        [current],
    );
    const progress = steps.length ? Math.max(0, (stepIdx + 1) / steps.length) : 0;
    const entitled = entitlement?.entitled === true;

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex flex-col"
            data-testid="live-canvas"
            style={{
                background:
                    'radial-gradient(70% 60% at 75% 15%, rgba(30,42,38,0.98) 0%, #0A0F0C 55%, #050806 100%)',
            }}
        >
            {/* Header */}
            <header className="flex items-center justify-between px-4 sm:px-6 lg:px-10 py-4 border-b border-white/15">
                <div className="flex items-center gap-3">
                    <button onClick={() => { stopAll(); onExit?.(); }} data-testid="lc-exit"
                        className="mono text-[10px] text-white hover:text-[#D4B3FF] flex items-center gap-1">
                        <X size={12} /> EXIT
                    </button>
                    <span className="text-[#9CA3AF] hidden sm:inline">·</span>
                    <div className="mono text-[10px] tracking-[0.3em] text-white hidden sm:flex items-center gap-2">
                        <PencilLine size={12} className="text-[#D4B3FF]" /> LIVE CANVAS TUTOR
                        {engine && <span className="text-[#8a957f]">({engine})</span>}
                    </div>
                </div>
                <div className="mono text-[9px] sm:text-[10px] text-[#D1D5DB]">Pro &amp; Premium Feature · ₹500+/mo plans</div>
            </header>

            <div className="flex-1 overflow-y-auto relative">
                {/* Paywall */}
                {entitlement && !entitled && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center p-4"
                        style={{ background: 'rgba(5,8,6,0.72)', backdropFilter: 'blur(10px)' }}
                        data-testid="lc-paywall">
                        <motion.div
                            initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                            className="max-w-md w-full rounded-lg border border-[#3e4438] bg-[#1a1e17]/95 p-8 text-center"
                            style={{ boxShadow: '0 40px 80px -25px rgba(0,0,0,0.8)' }}>
                            <div className="h-14 w-14 mx-auto rounded-full bg-[#B79CFF]/15 border border-[#B79CFF]/50 flex items-center justify-center">
                                <Lock size={22} className="text-[#D4B3FF]" />
                            </div>
                            <div className="mono text-[9px] tracking-[0.24em] text-[#D4B3FF] mt-4">PRO &amp; PREMIUM FEATURE</div>
                            <h2 className="display text-[#f0f4ee] text-3xl mt-2">Unlock Real-Time Live Canvas Tutor</h2>
                            <p className="text-[#c2ccbe] text-[13.5px] mt-3 leading-relaxed">
                                Experience real-time visual step-by-step math solving, synchronized voice
                                explanations, and interactive pause-and-ask — right on the chalkboard.
                            </p>
                            <div className="mono text-[10px] text-[#8a957f] mt-3">Available on plans ≥ ₹500/mo</div>
                            <button
                                onClick={() => {
                                    stopAll();
                                    onExit?.();
                                    navigate('/pricing');
                                }}
                                data-testid="lc-upgrade-cta"
                                className="mt-5 w-full py-3 rounded-sm mono text-[11px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] transition-colors flex items-center justify-center gap-2">
                                <Sparkles size={13} /> Upgrade Plan (Starting at ₹500/mo)
                            </button>
                            {entitlement.reason === 'auth_required' && (
                                <div className="mono text-[9px] text-[#8a957f] mt-3">Sign in to check your plan.</div>
                            )}
                        </motion.div>
                    </div>
                )}

                {/* Idle: problem input */}
                {entitled && phase === 'idle' && (
                    <div className="max-w-2xl mx-auto text-center pt-16 px-4" data-testid="lc-idle">
                        <div className="inline-flex items-center gap-2 mono text-[10px] text-[#D4B3FF] mb-4">
                            <Sparkles size={12} /> CHALKBOARD READY
                        </div>
                        <h2 className="display text-white" style={{ fontSize: 'clamp(30px, 3.6vw, 48px)', lineHeight: 1.08 }}>
                            Hand me an equation.
                            <br />
                            <span className="display-italic text-[#D4B3FF]">Watch it get solved, live.</span>
                        </h2>
                        <div className="mt-8 flex items-center gap-2 max-w-md mx-auto">
                            <input
                                value={problem}
                                onChange={(e) => setProblem(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && startCanvas()}
                                placeholder="e.g. 2x + 4 = 10"
                                data-testid="lc-problem-input"
                                className="flex-1 bg-white/5 border border-white/25 rounded-sm px-4 py-3 text-[#f0f4ee] text-[15px] outline-none focus:border-[#B79CFF]/70"
                            />
                            <button onClick={startCanvas} data-testid="lc-start"
                                className="px-5 py-3 rounded-sm mono text-[11px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] transition-colors flex items-center gap-2">
                                <Play size={12} fill="currentColor" /> Solve live
                            </button>
                        </div>
                        <div className="mono text-[9px] text-[#8a957f] mt-3">
                            Linear equations solve on the offline engine instantly · anything else drafts via Metis AI
                        </div>
                    </div>
                )}

                {entitled && phase === 'loading' && (
                    <div className="mono text-[11px] text-[#D1D5DB] text-center pt-24" data-testid="lc-loading">
                        Metis is preparing the canvas…
                    </div>
                )}

                {/* Canvas */}
                {entitled && ['streaming', 'paused', 'explaining', 'done'].includes(phase) && current && (
                    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="lc-canvas">
                        {/* Progress */}
                        <div className="flex items-center gap-3 mb-5">
                            <div className="flex-1 h-[4px] bg-white/10 rounded-full overflow-hidden" data-testid="lc-progress">
                                <motion.div className="h-full bg-[#B79CFF]" animate={{ width: `${progress * 100}%` }} transition={{ duration: 0.4 }} />
                            </div>
                            <div className="mono text-[10px] text-[#D1D5DB] whitespace-nowrap">
                                STEP {current.step_id} / {steps.length}
                            </div>
                        </div>

                        {/* Chalkboard */}
                        <div className="rounded-lg border border-white/20 bg-[#0d130f]/90 p-8 sm:p-12 relative overflow-hidden"
                            style={{ boxShadow: 'inset 0 0 80px rgba(0,0,0,0.6), 0 30px 60px -30px rgba(0,0,0,0.8)' }}>
                            <div className="mono text-[9px] tracking-[0.24em] text-[#8a957f] mb-6">
                                {current.action_type} · {current.animation_type}
                            </div>

                            {/* Before (struck) → After (revealed) */}
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`before-${stepIdx}`}
                                    initial={{ opacity: 0 }} animate={{ opacity: 0.55 }}
                                    className="text-[#9CA3AF] text-lg sm:text-2xl mb-4 line-through-none"
                                    data-testid="lc-latex-before"
                                    dangerouslySetInnerHTML={texHtml(current.latex_before, [], current.cancel_terms || [])}
                                />
                            </AnimatePresence>
                            <div className="overflow-hidden">
                                <motion.div
                                    key={`after-${stepIdx}`}
                                    initial={{ clipPath: 'inset(0 100% 0 0)' }}
                                    animate={phase === 'paused' || phase === 'explaining' ? {} : { clipPath: 'inset(0 0% 0 0)' }}
                                    transition={{ duration: (current.estimated_duration_ms || 3000) / 1000 / speed * 0.55, ease: 'linear' }}
                                    className="text-white text-2xl sm:text-4xl"
                                    data-testid="lc-latex-after"
                                    dangerouslySetInnerHTML={texHtml(current.latex_after, current.highlight_terms || [], [])}
                                />
                            </div>

                            {/* Narration caption with word-level sync */}
                            <div className="mt-8 text-[#D1D5DB] text-[15px] leading-relaxed" data-testid="lc-narration">
                                {captionWords.map((w, i) => (
                                    <span key={i} className={i <= wordIdx ? 'text-[#D4B3FF]' : ''}>{w} </span>
                                ))}
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-2.5 mt-5 flex-wrap">
                            {(phase === 'streaming' || phase === 'paused') && (
                                <button onClick={togglePause} data-testid="lc-pause-toggle"
                                    className="h-10 w-10 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white/10 transition-colors">
                                    {phase === 'paused' ? <Play size={15} fill="currentColor" /> : <Pause size={15} />}
                                </button>
                            )}
                            <button onClick={openAsk} disabled={phase === 'explaining'} data-testid="lc-explain-btn"
                                className="px-4 h-10 rounded-full mono text-[10px] border border-[#B79CFF]/60 text-[#D4B3FF] hover:bg-[#B79CFF]/15 transition-colors flex items-center gap-1.5 disabled:opacity-40">
                                <HelpCircle size={13} /> Wait, explain this step
                            </button>
                            <button onClick={() => setSpeed((s) => (s === 1 ? 1.25 : s === 1.25 ? 1.5 : 1))} data-testid="lc-speed"
                                className="px-4 h-10 rounded-full mono text-[10px] border border-white/25 text-[#D1D5DB] hover:bg-white/10 transition-colors flex items-center gap-1.5">
                                <Gauge size={13} /> {speed}x
                            </button>
                            <button onClick={() => { stopAll(); setPhase('idle'); setSteps([]); setStepIdx(-1); }} data-testid="lc-restart"
                                className="px-4 h-10 rounded-full mono text-[10px] border border-white/25 text-[#D1D5DB] hover:bg-white/10 transition-colors flex items-center gap-1.5">
                                <RotateCcw size={13} /> New problem
                            </button>
                            {phase === 'done' && (
                                <span className="mono text-[10px] text-emerald-400 ml-auto" data-testid="lc-done">SOLVED ✓</span>
                            )}
                        </div>

                        {/* Pause & Ask sub-panel */}
                        <AnimatePresence>
                            {phase === 'explaining' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
                                    className="mt-5 rounded-md border border-[#B79CFF]/50 bg-[#B79CFF]/10 p-5"
                                    data-testid="lc-ask-panel">
                                    <div className="mono text-[9px] tracking-[0.2em] text-[#D4B3FF] mb-2">PAUSED — ASK METIS ABOUT THIS STEP</div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && askMetis()}
                                            placeholder="e.g. Where did the 4 go?"
                                            data-testid="lc-ask-input"
                                            className="flex-1 bg-white/5 border border-white/25 rounded-sm px-3 py-2.5 text-[13px] text-[#f0f4ee] placeholder:text-[#8a957f] outline-none focus:border-[#B79CFF]/70"
                                        />
                                        <button onClick={askMetis} disabled={asking} data-testid="lc-ask-submit"
                                            className="px-4 py-2.5 rounded-sm mono text-[10px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] disabled:opacity-50">
                                            {asking ? 'Thinking…' : 'Ask'}
                                        </button>
                                    </div>
                                    {answer && (
                                        <div className="mt-3 text-[#e6ece2] text-[13.5px] leading-relaxed border-l-2 border-[#B79CFF]/70 pl-3" data-testid="lc-ask-answer">
                                            {answer}
                                        </div>
                                    )}
                                    <button onClick={resumeFromAsk} data-testid="lc-resume"
                                        className="mt-4 px-4 py-2 rounded-sm mono text-[10px] border border-white/30 text-white hover:bg-white/10 transition-colors">
                                        ▶ Resume Canvas
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </motion.div>,
        document.body
    );
}
